import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Check, Minus, Plus, ShoppingCart, Trash2, X } from 'lucide-react';

import { errorMessage } from '@/api/client';
import { customersApi, productsApi, salesApi } from '@/api/resources';
import {
  EmptyState,
  Loading,
  Money,
  PageHeader,
  StatCard,
  SubmitButton,
} from '@/components/common';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { formatGs } from '@/lib/currency';
import { firstDayOfMonth, formatDate, todayApi } from '@/lib/dates';

const NO_CUSTOMER = 'consumidor-final';

export default function Ventas() {
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [range, setRange] = useState({ from: firstDayOfMonth(), to: todayApi() });
  const [statusFilter, setStatusFilter] = useState('todas');
  const [dialogOpen, setDialogOpen] = useState(false);

  // Carrito de la venta en curso
  const [cart, setCart] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [customerId, setCustomerId] = useState(NO_CUSTOMER);
  const [notes, setNotes] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');

  const loadSales = useCallback(async () => {
    setLoading(true);
    try {
      setSales(await salesApi.list({ date_from: range.from, date_to: range.to }));
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudieron cargar las ventas'));
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to]);

  const loadCatalog = useCallback(async () => {
    try {
      const [productList, customerList] = await Promise.all([
        productsApi.list(),
        customersApi.list(),
      ]);
      setProducts(productList);
      setCustomers(customerList);
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo cargar el catálogo'));
    }
  }, []);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const visible = useMemo(() => {
    if (statusFilter === 'todas') return sales;
    return sales.filter((s) => s.status === statusFilter);
  }, [sales, statusFilter]);

  const totals = useMemo(() => {
    const completed = sales.filter((s) => s.status === 'completed');
    return {
      revenue: completed.reduce((sum, s) => sum + s.total, 0),
      profit: completed.reduce((sum, s) => sum + s.profit, 0),
      count: completed.length,
      scheduled: sales.filter((s) => s.status === 'scheduled').length,
    };
  }, [sales]);

  // --- Carrito ---

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartProfit = cart.reduce((sum, item) => sum + (item.price - item.cost) * item.quantity, 0);

  const addToCart = (product) => {
    setCart((current) => {
      const existing = current.find((i) => i.product_id === product.id);
      if (existing) {
        if (existing.quantity >= product.stock) {
          toast.error(`Sólo hay ${product.stock} unidad(es) de ${product.name}`);
          return current;
        }
        return current.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      if (product.stock < 1) {
        toast.error(`${product.name} no tiene stock`);
        return current;
      }
      return [
        ...current,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          cost: product.cost,
          stock: product.stock,
          quantity: 1,
        },
      ];
    });
    setProductSearch('');
  };

  const changeQuantity = (productId, delta) => {
    setCart((current) =>
      current.flatMap((item) => {
        if (item.product_id !== productId) return [item];
        const quantity = item.quantity + delta;
        if (quantity < 1) return [];
        if (quantity > item.stock) {
          toast.error(`Sólo hay ${item.stock} unidad(es)`);
          return [item];
        }
        return [{ ...item, quantity }];
      })
    );
  };

  const resetCart = () => {
    setCart([]);
    setCustomerId(NO_CUSTOMER);
    setNotes('');
    setScheduledDate('');
    setProductSearch('');
  };

  const openNew = () => {
    resetCart();
    setDialogOpen(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (cart.length === 0) {
      toast.error('Agregá al menos un producto');
      return;
    }

    setSaving(true);
    try {
      await salesApi.create({
        items: cart.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer_id: customerId === NO_CUSTOMER ? null : customerId,
        notes: notes.trim() || null,
        scheduled_date: scheduledDate || null,
      });
      toast.success(
        scheduledDate
          ? 'Venta agendada. El stock se descuenta al confirmarla.'
          : 'Venta registrada, stock descontado y caja actualizada'
      );
      setDialogOpen(false);
      resetCart();
      loadSales();
      loadCatalog();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo registrar la venta'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async (sale) => {
    try {
      await salesApi.confirm(sale.id);
      toast.success('Venta confirmada: stock descontado y caja actualizada');
      loadSales();
      loadCatalog();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo confirmar la venta'));
    }
  };

  const handleDelete = async (sale) => {
    const message =
      sale.status === 'completed'
        ? '¿Eliminar esta venta? Se devuelve el stock y se revierte el movimiento de caja.'
        : '¿Eliminar esta venta agendada?';
    if (!window.confirm(message)) return;

    try {
      await salesApi.remove(sale.id);
      toast.success('Venta eliminada');
      loadSales();
      loadCatalog();
    } catch (error) {
      toast.error(errorMessage(error, 'No se pudo eliminar la venta'));
    }
  };

  const term = productSearch.trim().toLowerCase();
  const searchResults = term
    ? products
        .filter(
          (p) =>
            p.stock > 0 &&
            (p.name.toLowerCase().includes(term) || p.sku.toLowerCase().includes(term))
        )
        .slice(0, 6)
    : [];

  return (
    <div className="space-y-6">
      <PageHeader title="Ventas" subtitle="El stock y la caja se actualizan solos">
        <Button onClick={openNew} className="rounded-sm">
          <Plus size={18} className="mr-2" />
          Nueva venta
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Facturado"
          value={formatGs(totals.revenue)}
          hint={`${totals.count} venta(s)`}
          icon={ShoppingCart}
        />
        <StatCard label="Ganancia" value={formatGs(totals.profit)} tone="positive" />
        <StatCard
          label="Ticket promedio"
          value={formatGs(totals.count ? Math.round(totals.revenue / totals.count) : 0)}
        />
        <StatCard
          label="Agendadas"
          value={totals.scheduled}
          hint={totals.scheduled ? 'Pendientes de confirmar' : 'Ninguna pendiente'}
          tone={totals.scheduled ? 'warning' : 'default'}
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-end gap-3 rounded-sm border border-border bg-card p-4">
        <div className="space-y-1">
          <Label className="text-xs">Desde</Label>
          <Input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="rounded-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Hasta</Label>
          <Input
            type="date"
            value={range.to}
            min={range.from}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="rounded-sm"
          />
        </div>
        <div className="min-w-[150px] flex-1 space-y-1">
          <Label className="text-xs">Estado</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              <SelectItem value="completed">Completadas</SelectItem>
              <SelectItem value="scheduled">Agendadas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={ShoppingCart}
          title="Sin ventas en este período"
          description="Registrá una venta y el stock y la caja se actualizan automáticamente."
          action={
            <Button onClick={openNew} className="rounded-sm">
              <Plus size={18} className="mr-2" />
              Nueva venta
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {visible.map((sale) => {
            const scheduled = sale.status === 'scheduled';
            return (
              <div
                key={sale.id}
                className={`rounded-sm border bg-card p-4 ${
                  scheduled ? 'border-amber-300 dark:border-amber-900' : 'border-border'
                }`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold">
                        {sale.customer_name || 'Consumidor final'}
                      </span>
                      {scheduled && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                          Agendada {formatDate(sale.scheduled_date)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(sale.date)} · {sale.items.length} producto(s)
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <Money value={sale.total} className="block font-bold" />
                      <span className="text-xs text-emerald-600 dark:text-emerald-400">
                        +{formatGs(sale.profit)}
                      </span>
                    </div>
                    {scheduled && (
                      <Button
                        size="icon"
                        variant="outline"
                        className="rounded-sm"
                        onClick={() => handleConfirm(sale)}
                        title="Confirmar venta"
                      >
                        <Check size={16} className="text-emerald-600" />
                      </Button>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDelete(sale)}
                      aria-label="Eliminar"
                    >
                      <Trash2 size={15} className="text-red-500" />
                    </Button>
                  </div>
                </div>

                <div className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground">
                  {sale.items.map((item) => (
                    <div key={item.product_id} className="flex justify-between gap-2">
                      <span className="truncate">
                        {item.quantity}× {item.product_name}
                      </span>
                      <span className="shrink-0 tabular-nums">
                        {formatGs(item.price * item.quantity)}
                      </span>
                    </div>
                  ))}
                  {sale.notes && <p className="mt-1 italic">{sale.notes}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Nueva venta */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nueva venta</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Buscador de productos */}
            <div className="space-y-2">
              <Label htmlFor="v-search">Agregar producto</Label>
              <Input
                id="v-search"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Buscá por nombre o SKU…"
                className="rounded-sm"
                autoComplete="off"
              />
              {searchResults.length > 0 && (
                <div className="divide-y divide-border rounded-sm border border-border">
                  {searchResults.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => addToCart(product)}
                      className="flex w-full items-center justify-between gap-2 p-2 text-left text-sm hover:bg-accent"
                    >
                      <span className="min-w-0 truncate">
                        {product.name}
                        <span className="ml-2 text-xs text-muted-foreground">
                          stock {product.stock}
                        </span>
                      </span>
                      <span className="shrink-0 font-medium tabular-nums">
                        {formatGs(product.price)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {term && searchResults.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Sin productos con stock que coincidan.
                </p>
              )}
            </div>

            {/* Carrito */}
            {cart.length > 0 && (
              <div className="divide-y divide-border rounded-sm border border-border">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex items-center gap-2 p-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{item.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatGs(item.price)} c/u
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-7 w-7 rounded-sm"
                        onClick={() => changeQuantity(item.product_id, -1)}
                      >
                        <Minus size={13} />
                      </Button>
                      <span className="w-8 text-center text-sm font-bold tabular-nums">
                        {item.quantity}
                      </span>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        className="h-7 w-7 rounded-sm"
                        onClick={() => changeQuantity(item.product_id, 1)}
                      >
                        <Plus size={13} />
                      </Button>
                    </div>
                    <span className="w-24 shrink-0 text-right text-sm font-bold tabular-nums">
                      {formatGs(item.price * item.quantity)}
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() =>
                        setCart((c) => c.filter((i) => i.product_id !== item.product_id))
                      }
                    >
                      <X size={13} />
                    </Button>
                  </div>
                ))}
                <div className="flex items-center justify-between bg-muted p-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-xl font-black tracking-tighter">{formatGs(cartTotal)}</p>
                  </div>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">
                    Ganancia: {formatGs(cartProfit)}
                  </p>
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CUSTOMER}>Consumidor final</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="v-date">Agendar para (opcional)</Label>
                <Input
                  id="v-date"
                  type="date"
                  min={todayApi()}
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="rounded-sm"
                />
              </div>
            </div>

            {scheduledDate && (
              <p className="rounded-sm bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                La venta queda agendada: no descuenta stock ni entra a caja hasta que la confirmes.
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="v-notes">Notas</Label>
              <Textarea
                id="v-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="rounded-sm"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-sm"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <SubmitButton loading={saving} disabled={saving || cart.length === 0}>
                {scheduledDate ? 'Agendar venta' : `Cobrar ${formatGs(cartTotal)}`}
              </SubmitButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
